import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components/macro";
import { PrimaryButtonMedium, TertiaryButtonSmall } from "../Buttons/Buttons";
import { useStoreState } from "../../util/util";
import useSWR from "swr";
import type { ChatMessage } from "./MessageTimeline";
import { MessageTimeline } from "./MessageTimeline";
import {
  ChatIcon,
  CollapseIcon,
  ExpandIcon,
  LoadingIcon,
  NewXIcon,
} from "../Icons/Icons";
import { SendMessageToChatBot } from "./SendMessageToChatBot";
import { t } from "i18next";
import axios from "axios";
import type { StorefrontChatbotConfiguration } from "../../types/types";
import Markdown from "markdown-to-jsx";
import { useAuthContext } from "../Auth";

export type ChatBotCSSProps = {
  isOpen?: boolean;
  isExpanded?: boolean;
};

export const ChatBotContainer = styled.div<ChatBotCSSProps>`
  position: fixed;
  z-index: 900;
  bottom: ${(props) => (props.isOpen ? "20px" : "0")};
  right: ${(props) => (props.isOpen ? "20px" : "0")};
  border: ${(props) =>
    props.isOpen
      ? `${props.theme.resolve("border_md")} solid ${props.theme.primaryBorder}`
      : "0"};
  width: ${(props) =>
    props.isOpen ? (props.isExpanded ? "500px" : "380px") : "275px"};
  height: ${(props) =>
    props.isOpen ? (props.isExpanded ? "80vh" : "550px") : "90px"};
  background-color: ${(props) => (props.isOpen ? "#fff" : "transparent")};
  border-radius: ${(props) =>
    props.isOpen ? props.theme.resolve("radius_md") : "0"};
  box-shadow: ${(props) =>
    props.isOpen ? "0px 1px 20px 0px #6f777f66" : "none"};
  overflow: hidden;
  transition: width 0.3s, height 0.3s, background-color 0.3s, border-radius 0.3s;
  display: flex;
  flex-direction: column;
`;

export const ChatBotIcon = styled(PrimaryButtonMedium)<ChatBotCSSProps>`
  position: absolute;
  top: ${(props) => (props.isOpen ? "5px" : "unset")};
  right: ${(props) => (props.isOpen ? "5px" : "unset")};
  display: ${(props) => (props.isOpen ? "none" : "flex")};
  border-radius: 25px;
  margin: ${({ theme }) => theme.resolve("spacing_2xl")};
  box-shadow: ${(props) =>
    props.isOpen ? "none" : "0px 1px 20px 0px #6f777f66"};
  align-items: center;
  justify-content: space-around;
`;

export const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: auto;
`;

export const ChatBotContent = styled.div<ChatBotCSSProps>`
  display: ${(props) => (props.isOpen ? "flex" : "none")};
  transition: all 0.3s ease-out;
  padding: ${({ theme }) => theme.resolve("spacing_2xl")};
  flex-grow: 1;
  overflow-y: auto;
  flex-direction: column;
  position: relative;
`;

export const TopSection = styled.div`
  display: flex;
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  align-items: center;
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.secondaryBorder};
`;
export const BottomSection = styled.div<ChatBotCSSProps>`
  display: ${(props) => (props.isOpen ? "flex" : "none")};
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  border-top: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.secondaryBorder};
  flex-direction: column;
  width: 100%;
`;

export const DisclaimerText = styled.div`
  font-size: 11px;
  text-align: center;
  margin-top: ${({ theme }) => theme.resolve("spacing_md")};
  width: 100%;
  // ReactMarkdown introduces a p tag with padding by default
  p {
    margin-block-end: 0;
    margin-block-start: 0;
    padding: ${({ theme }) => theme.resolve("spacing_none")};
  }
`;

export const TypingAnimation = styled.div`

	position:relative;
	text-align:center;
	width:80px;
	height:100px;
	span {
		display:inline-block;
		width:6px;
		height:6px;
		border-radius:50%;
		margin-right:3px;
		background:#303131;
		animation: wave 0.9s linear infinite;

		&:nth-child(2) {
			animation-delay: -0.7s;
		}

		&:nth-child(3) {
			animation-delay: -0.5s;
		}
	}
}

@keyframes wave {
	0%, 60%, 100% {
		transform: initial;
	}

	30% {
		transform: translateY(-12px);
	}
  }
`;

export const ButtonSection = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
`;

export function ChatBot() {
  const { storefront_id } = useStoreState();
  const { user, role } = useAuthContext();
  const storageKey = `chat_v2_${storefront_id}_${
    user?.id || "anonymous"
  }_${role}`;
  return <ChatBotSession key={storageKey} storageKey={storageKey} />;
}

function ChatBotSession({ storageKey }: { storageKey: string }) {
  const [isOpen, setIsOpen] = useState(
    JSON.parse(sessionStorage.getItem("chatbotIsOpen") as string) ?? false
  );
  const [isExpanded, setIsExpanded] = useState(
    JSON.parse(sessionStorage.getItem("chatbotIsExpanded") as string) ?? false
  );
  const { storefront_id } = useStoreState();
  const [threadId, updateThreadId] = useState<string | null>(
    sessionStorage.getItem(storageKey)
  );
  const setThreadId = (id: string | null) => {
    if (id) sessionStorage.setItem(storageKey, id);
    else sessionStorage.removeItem(storageKey);
    updateThreadId(id);
  };
  const [threadMessages, setThreadMessages] = useState<ChatMessage[]>([]);
  const [inProgress, setInProgress] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );
  const resetChat = () => {
    setThreadId(null);
    setThreadMessages([]);
    setInProgress(false);
  };

  const toggleChatBot = () => {
    sessionStorage.setItem("chatbotIsOpen", (!isOpen).toString());
    setIsOpen(!isOpen);
  };
  const toggleExpand = () => {
    sessionStorage.setItem("chatbotIsExpanded", (!isExpanded).toString());
    setIsExpanded(!isExpanded);
  };

  const addErrorMessage = useCallback(
    (message = "Something went wrong, please try again") => {
      setThreadMessages((prev) => [
        ...prev,
        {
          id: Math.random().toString(),
          run_id: null,
          role: "error",
          created_at: Date.now().toString(),
          thread_id: threadId || "noThread",
          completed_at: null,
          status: "completed",
          content: [
            {
              type: "text",
              text: {
                value: message,
              },
            },
          ],
        },
      ]);
    },
    [threadId]
  );

  const { data: config, error: configError } =
    useSWR<StorefrontChatbotConfiguration>(
      `/v2/storefronts/${storefront_id}/chatbots`
    );

  const defaultMessage: ChatMessage = {
    completed_at: null,
    content: [
      {
        text: {
          value: config?.intro_message || t("Hello, How may I help you"),
        },
        type: "text",
      },
    ],
    created_at: "1720994376",
    id: "0",
    role: "assistant",
    run_id: null,
    status: null,
    thread_id: "noThread",
  };
  const shouldFetchMessages = config?.is_enabled && isOpen && threadId;

  const { data: messages, error: messagesError } = useSWR(
    shouldFetchMessages
      ? `/v2/storefronts/${storefront_id}/chatbots/threads/${threadId}/messages?order_by=asc&limit=100`
      : null,
    { revalidateOnFocus: false }
  );

  useEffect(() => {
    if (messagesError && [404, 410].includes(messagesError.response?.status)) {
      resetChat();
      addErrorMessage(
        "Your previous chat is no longer available. Please start a new chat."
      );
    } else if (messagesError || configError) {
      addErrorMessage("Something went wrong, please try again");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesError, configError, addErrorMessage]);

  const handleMessageInProgress = (inProgress: boolean, message?: string) => {
    setInProgress(inProgress);
    if (message) {
      setThreadMessages((prev) => [
        ...prev,
        {
          id: "temp-" + Date.now(),
          run_id: "temp",
          role: "user",
          created_at: Date.now().toString(),
          thread_id: threadId || "noThread",
          completed_at: null,
          status: null,
          content: [
            {
              type: "text",
              text: {
                value: message,
              },
            },
          ],
        },
      ]);
      if (contentRef?.current) {
        contentRef.current.scrollTop = contentRef.current.scrollHeight;
      }
    }
  };

  const fetchLastMessageData = async (threadId: string, runId: string) => {
    try {
      const runIdResponse = await axios.get(
        `/v2/storefronts/${storefront_id}/chatbots/threads/${threadId}/messages?run_id=${runId}&order_by=asc&limit=100`
      );
      if (!mounted.current) return;
      const answers: ChatMessage[] = runIdResponse.data?.data || [];
      setInProgress(false);
      setThreadMessages((prev) => [
        ...prev.filter((m) => !answers.some((answer) => answer.id === m.id)),
        ...answers,
      ]);
      if (!answers.length) {
        addErrorMessage("No answer was returned. Please try again.");
      }
      if (contentRef?.current) {
        contentRef.current.scrollTop = contentRef.current.scrollHeight;
      }
    } catch (error) {
      if (!mounted.current) return;
      addErrorMessage("something went wrong");
      setInProgress(false);
    }
  };

  useEffect(() => {
    if (messages) {
      setThreadMessages(messages.data);
    }
  }, [messages]);

  return (
    <>
      {config && config.is_enabled && (
        <ChatBotContainer isOpen={isOpen} isExpanded={isExpanded}>
          <ChatBotIcon isOpen={isOpen} onClick={toggleChatBot}>
            <ChatIcon />
            <span style={{ marginLeft: "10px" }}>
              {t("Chat with an Assistant")}
            </span>
          </ChatBotIcon>
          {isOpen && (
            <TopSection>
              <LogoContainer>
                {config?.avatar && (
                  <img
                    src={config?.avatar}
                    alt="logo"
                    style={{
                      maxWidth: "70px",
                      maxHeight: "50px",
                      margin: "0 10px 0 0",
                    }}
                  />
                )}
                {config?.name}
              </LogoContainer>
              <ButtonSection>
                <TertiaryButtonSmall onClick={toggleExpand}>
                  {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                </TertiaryButtonSmall>
                <TertiaryButtonSmall onClick={toggleChatBot}>
                  <NewXIcon />
                </TertiaryButtonSmall>
              </ButtonSection>
            </TopSection>
          )}
          <ChatBotContent
            isExpanded={isExpanded}
            isOpen={isOpen}
            ref={contentRef}
          >
            <MessageTimeline
              contentRef={contentRef}
              messages={
                (threadId && messages) ||
                (threadId && threadMessages.length < 1 && !messagesError)
                  ? threadMessages
                  : [defaultMessage, ...threadMessages]
              }
            />
            {threadId && threadMessages.length < 1 && !messagesError && (
              <div
                style={{
                  display: "flex",
                  height: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LoadingIcon width={30} />
              </div>
            )}
            {inProgress && (
              <TypingAnimation>
                <span></span>
                <span></span>
                <span></span>
              </TypingAnimation>
            )}
          </ChatBotContent>
          {((threadId &&
            (threadMessages.length > 0 || messagesError || messages)) ||
            !threadId) && (
            <BottomSection isOpen={isOpen} isExpanded={isExpanded}>
              <SendMessageToChatBot
                threadId={threadId}
                activeRunId={messages?.active_run_id}
                resetChat={resetChat}
                addErrorMessage={addErrorMessage}
                fetchLastMessageData={fetchLastMessageData}
                handleMessageInProgress={handleMessageInProgress}
                handleSetThreadId={setThreadId}
                inProgress={inProgress}
              />

              {config?.inside_login_disclaimer && (
                <DisclaimerText>
                  <Markdown>{config?.inside_login_disclaimer}</Markdown>
                </DisclaimerText>
              )}
            </BottomSection>
          )}
        </ChatBotContainer>
      )}
    </>
  );
}
